import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import { isNotNil } from 'es-toolkit'
import { useEffect, useEffectEvent, useMemo, useState } from 'react'
import { z } from 'zod'
import { assetQuery } from '../../data/assets.ts'
import { type AssetLocation, useAssetLocations } from '../../data/locations.tsx'
import { useProtections } from '../../data/protections/useProtections.ts'
import { CreateReportForm } from '../../features/reports/create-form/CreateReportForm.tsx'
import {
	type ReportType,
	ReportTypeSchema,
	useAllowUnprotectedAssets,
	useMaxAssetsOf,
	useReportTypes,
} from '../../features/reports/create-form/reportTypes.tsx'
import { UrlListSchema } from '../-searchSchemas.ts'

export const Route = createFileRoute('/_app/reports/add')({
	validateSearch: z.object({
		locationIds: UrlListSchema,
		type: ReportTypeSchema.optional(),
	}),
	component: RouteComponent,
	beforeLoad: async ({ context: { queryClient, impersonation, isAdmin, isLimitedAdmin, sub } }) => {
		if ((!isAdmin && !isLimitedAdmin) || (!impersonation && !sub.features.includes('analyze'))) {
			throw redirect({ to: '/' })
		}
		await queryClient.ensureQueryData(assetQuery(impersonation, { enabled: sub.features.includes('protections') }))
	},
})

const inUrl = (ids: string[]) => (ids.length > 0 ? ids : undefined)

const idsTakenBy = (ids: string[], maxAssets: number) => (maxAssets > 1 ? ids : ids.slice(0, 1))

const NO_LOCATIONS: AssetLocation[] = []

function RouteComponent() {
	const { locationIds, type } = useSearch({ from: '/_app/reports/add' })
	const { data: locations, isLoading } = useAllowedReportLocations(useAllowUnprotectedAssets(type))
	const navigate = useNavigate({ from: '/reports/add' })

	const maxAssetsOf = useMaxAssetsOf()
	const idsInUrl = locationIds ?? []
	// A report type that takes one asset keeps the first of the assets that another report type had selected
	const idsFor = (reportType: ReportType | undefined) => idsTakenBy(idsInUrl, maxAssetsOf(reportType))
	const maxAssets = maxAssetsOf(type)
	const requestedLocationIds = useMemo(() => idsTakenBy(locationIds ?? [], maxAssets), [locationIds, maxAssets])
	const selectedLocations = useMemo(
		() => requestedLocationIds.map((id) => locations.find((loc) => loc.id === id)).filter(isNotNil),
		[requestedLocationIds, locations],
	)
	const selectLocationIds = (ids: string[]) =>
		navigate({
			to: '.',
			search: (prev) => ({ ...prev, locationIds: inUrl(ids) }),
			replace: true,
		})
	const onLocationsSelected = (selected: AssetLocation[]) => void selectLocationIds(selected.map((location) => location.id))
	const reportTypes = useReportTypes({ locations, selectedLocations, onLocationsSelected })
	const selectedReportType = reportTypes.find((entry) => entry.type === type)
	// Until the user has chosen a report type, the form is built for the first available one
	const activeReportType = selectedReportType ?? reportTypes[0]

	const [isInitialized, setIsInitialized] = useState(false)

	const initialize = useEffectEvent(async () => {
		try {
			// If the location search params name locations that are not valid or that the report type does not take, then remove those
			if (selectedLocations.length < idsInUrl.length) {
				await selectLocationIds(selectedLocations.map((location) => location.id))
			}

			// If no location is requested and only a single location is selectable, then select it automatically
			if (requestedLocationIds.length === 0 && locations.length === 1) {
				await selectLocationIds(locations.map((location) => location.id))
			}

			// With a single report type there is nothing to choose, so keep the url canonical
			if (reportTypes.length === 1 && activeReportType && type !== activeReportType.type) {
				await navigate({ to: '.', search: (prev) => ({ ...prev, type: activeReportType.type }), replace: true })
			}
		} finally {
			setIsInitialized(true)
		}
	})

	useEffect(() => {
		if (isLoading || isInitialized) return
		void initialize()
	}, [isLoading, isInitialized])

	if (!isInitialized || !activeReportType) return null

	return (
		<CreateReportForm
			reportType={activeReportType.type}
			config={activeReportType.config}
			locations={locations}
			selectedLocations={selectedLocations}
			onLocationsSelected={onLocationsSelected}
			reportTypeSelection={
				reportTypes.length > 1
					? {
							options: reportTypes,
							selected: selectedReportType?.type,
							onSelect: (reportType) =>
								void navigate({
									to: '.',
									search: (prev) => ({ ...prev, type: reportType, locationIds: inUrl(idsFor(reportType)) }),
									replace: true,
								}),
						}
					: undefined
			}
		/>
	)
}

const useAllowedReportLocations = (allowUnprotectedAssets: boolean) => {
	const { data: locations, isPending: isLoadingLocations } = useAssetLocations()
	const { isLoading: isLoadingProtections, assetProtections } = useProtections()
	const protectedLocations = useMemo(
		() =>
			locations.filter((location) => {
				const locationProtections = assetProtections[location.assetVrn]
				return !!locationProtections && locationProtections.length > 0
			}),
		[locations, assetProtections],
	)

	if (allowUnprotectedAssets) {
		return { data: locations, isLoading: isLoadingLocations }
	}

	if (isLoadingProtections) {
		return { data: NO_LOCATIONS, isLoading: true }
	}

	return { data: protectedLocations, isLoading: false }
}
